import HttpClient from '../http/HttpClient';
import { LEAD_SERVICE_HOST, LEAD_API_PREFIX } from '../constants/RestConstants';
import type {
  PartnerIndianMarketCharges,
  PartnerPlanExecutionView,
  PartnerPlanPurchaseView,
  PartnerPlanResultView,
  PartnerPlanSellView,
  PartnerPortalDashboard,
} from '../../models/PartnerPortalTypes';
import type { PlanMarketValuation } from '../../models/MarketValuationTypes';
import { mapMarketTechnicals } from '../../utils/marketTechnicals';
import type { StockPlanStatus, StockPickHoldingType } from '../../models/StockPickTypes';

type ApiEnvelope<T> = { data: T; message?: string; statusCode: number };

function mapCharges(c: any): PartnerIndianMarketCharges {
  return {
    stt: c?.stt ?? 0,
    brokerage: c?.brokerage ?? 0,
    gstOnBrokerage: c?.gstOnBrokerage ?? 0,
    exchangeTxnCharges: c?.exchangeTxnCharges ?? 0,
    sebiTurnoverFees: c?.sebiTurnoverFees ?? 0,
    stampDuty: c?.stampDuty ?? 0,
  };
}

function mapPurchase(raw: any): PartnerPlanPurchaseView | undefined {
  if (!raw) return undefined;
  return {
    tradeDate: raw.tradeDate ?? '',
    quantity: raw.quantity ?? 0,
    pricePerShare: raw.pricePerShare ?? 0,
    grossAmount: raw.grossAmount ?? 0,
    totalCharges: raw.totalCharges ?? 0,
    charges: mapCharges(raw.charges),
    comments: raw.comments,
  };
}

function mapSell(raw: any): PartnerPlanSellView {
  return {
    tradeDate: raw.tradeDate ?? '',
    quantity: raw.quantity ?? 0,
    pricePerShare: raw.pricePerShare ?? 0,
    grossProceeds: raw.grossProceeds ?? 0,
    totalCharges: raw.totalCharges ?? 0,
    capitalGainsType: raw.capitalGainsType,
    charges: mapCharges(raw.charges),
    comments: raw.comments,
  };
}

function mapExecution(raw: any): PartnerPlanExecutionView | undefined {
  if (!raw) return undefined;
  const purchases = (raw.purchases ?? []).map(mapPurchase);
  return {
    purchases,
    purchase: purchases[0] ?? mapPurchase(raw.purchase),
    sells: (raw.sells ?? []).map(mapSell),
    sharesBought: raw.sharesBought ?? 0,
    sharesSold: raw.sharesSold ?? 0,
    sharesRemaining: raw.sharesRemaining ?? 0,
    totalPurchaseValue: raw.totalPurchaseValue ?? 0,
    totalSellProceeds: raw.totalSellProceeds ?? 0,
    totalCharges: raw.totalCharges ?? 0,
    realizedGainOnPlan: raw.realizedGainOnPlan,
  };
}

function mapMarketValuation(raw: any): PlanMarketValuation | undefined {
  if (!raw) return undefined;
  return {
    planId: raw.planId,
    nseCode: raw.nseCode,
    sharesHeld: raw.sharesHeld ?? 0,
    lastClosePrice: raw.lastClosePrice != null ? Number(raw.lastClosePrice) : null,
    priceAvailable: Boolean(raw.priceAvailable),
    priceFetchedAt: raw.priceFetchedAt,
    investedAmount: raw.investedAmount ?? 0,
    currentValue: raw.currentValue ?? 0,
    unrealizedPnl: raw.unrealizedPnl ?? 0,
    unrealizedPnlPct: raw.unrealizedPnlPct != null ? Number(raw.unrealizedPnlPct) : null,
    partnerCurrentValue: raw.partnerCurrentValue != null ? Number(raw.partnerCurrentValue) : null,
    partnerInvestedAmount: raw.partnerInvestedAmount != null ? Number(raw.partnerInvestedAmount) : null,
    partnerUnrealizedPnl: raw.partnerUnrealizedPnl != null ? Number(raw.partnerUnrealizedPnl) : null,
    technicals: mapMarketTechnicals(raw.technicals),
  };
}

function mapPlanResult(raw: any): PartnerPlanResultView | undefined {
  if (!raw) return undefined;
  return {
    resultDate: raw.resultDate,
    investedAmount: raw.investedAmount ?? 0,
    quantity: raw.quantity ?? 0,
    taxAmount: raw.taxAmount ?? 0,
    profitAmount: raw.profitAmount ?? 0,
    learning: raw.learning,
    yourInvestedShare: raw.yourInvestedShare ?? 0,
    yourTaxShare: raw.yourTaxShare ?? 0,
    yourProfitShare: raw.yourProfitShare ?? 0,
  };
}

/** Map API dashboard payload to frontend types. */
function mapDashboard(raw: any): PartnerPortalDashboard {
  return {
    profile: {
      id: raw.profile.id,
      name: raw.profile.name,
      email: raw.profile.email ?? '',
      phone: raw.profile.phone,
      status: raw.profile.status ?? 'ACTIVE',
      portalAccessEnabled: Boolean(raw.profile.portalAccessEnabled),
      totalContributed: raw.profile.totalContributed ?? 0,
      totalAllocated: raw.profile.totalAllocated ?? 0,
      availableBalance: raw.profile.availableBalance ?? 0,
      memberSince: raw.profile.memberSince?.slice?.(0, 10),
    },
    pnlSummary: {
      realizedProfit: raw.pnlSummary?.realizedProfit ?? 0,
      realizedLoss: raw.pnlSummary?.realizedLoss ?? 0,
      unrealizedProfit: raw.pnlSummary?.unrealizedProfit ?? 0,
      unrealizedLoss: raw.pnlSummary?.unrealizedLoss ?? 0,
    },
    allocations: (raw.allocations ?? []).map((a: any) => ({
      id: a.id,
      stockName: a.stockName,
      nseCode: a.nseCode,
      bseCode: a.bseCode,
      domain: a.domain,
      capLabel: a.capLabel,
      planId: a.planId,
      planCode: a.planCode,
      planLabel: a.planLabel,
      planStatus: a.planStatus as StockPlanStatus,
      holdingType: a.holdingType as StockPickHoldingType,
      holdingTypeLabel: a.holdingTypeLabel ?? a.holdingType,
      allocatedAmount: a.allocatedAmount ?? 0,
      allocationDate: a.allocationDate ?? '',
      allocationNote: a.allocationNote,
      plannedCapital: a.plannedCapital,
      totalCapitalOnPlan: a.totalCapitalOnPlan ?? 0,
      shareOfPlanPct: a.shareOfPlanPct ?? 0,
      planAndReasons: a.planAndReasons,
      exitStrategy: a.exitStrategy,
      riskMitigation: a.riskMitigation,
      execution: mapExecution(a.execution),
      planResult: mapPlanResult(a.planResult),
      purchaseTotalCost: a.purchaseTotalCost != null ? Number(a.purchaseTotalCost) : undefined,
      marketValuation: mapMarketValuation(a.marketValuation),
      pnl: {
        realizedProfit: a.pnl?.realizedProfit ?? 0,
        realizedLoss: a.pnl?.realizedLoss ?? 0,
        unrealizedProfit: a.pnl?.unrealizedProfit ?? 0,
        unrealizedLoss: a.pnl?.unrealizedLoss ?? 0,
      },
    })),
    contributions: (raw.contributions ?? []).map((c: any) => ({
      id: c.id,
      amount: c.amount,
      contributionDate:
        typeof c.contributionDate === 'string'
          ? c.contributionDate
          : Array.isArray(c.contributionDate)
            ? `${c.contributionDate[0]}-${String(c.contributionDate[1]).padStart(2, '0')}-${String(c.contributionDate[2]).padStart(2, '0')}`
            : '',
      referenceNote: c.referenceNote,
    })),
    activity: (raw.activity ?? []).map((item: any) => ({
      id: item.id,
      date: item.date,
      type: item.type,
      title: item.title,
      description: item.description,
    })),
  };
}

export async function getPartnerDashboard(): Promise<PartnerPortalDashboard> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/partner/dashboard`,
  });
  return mapDashboard((response.data as ApiEnvelope<unknown>).data);
}
